import { Box, Button, Card, CardHeader, Divider, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material"
import RenderIf, { ClaimType, ClaimValue } from "../../common/RenderIf"
import SimpleDialog from "../../common/SimpleDialog"
import AddIcon from '@mui/icons-material/Add';
import EyeOutline from 'mdi-material-ui/EyeOutline'
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AttachmentList from './AttachmentList'
import FileCopyIcon from '@mui/icons-material/FileCopy';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close'
import { ApiResponseType } from "../../../types";
import ApplicationStore from "../../../stores/ApplicationStore";
import { CadenceTemplate, CadenceTemplateVariant, loadPlaybooks, PlaybookLibrary, PlaybookStore, saveTemplate, saveTemplateVariant } from '../PlaybookStore'
import useAwaitableComponent from "../../../hooks/useAwaitableComponent";
import { client } from "../../../utils/axios-client";
import { useState, MouseEvent } from "react";
import NameDialog from "../NameDialog";
import EditCadenceTemplateVariant from "./EditCadenceTemplateVariant";
import AttachmentVariantList from "./AttachmenVariantList";
import VariantFilters from "./VariantFilters";
import ConfirmationDialog from "../../common/ConfirmationDialog";

type Props = {
    open: boolean,
    onClose: () => void
}

const VariantListDialog = ({ open, onClose }: Props) => {
    const selectedTemplate = PlaybookStore.useState(s => s.selectedTemplate);
    const template = PlaybookStore.useState(s => s.selectedTemplate);
    const playbook = PlaybookStore.useState(s => s.selectedPlaybook);
    const [status, execute, resolve, reject, reset] = useAwaitableComponent();
    const [addVariantDialogOpen, setAddVariantDialogOpen] = useState(false);
    const [editTemplateVariantOpen, setEditTemplateVariantOpen] = useState(false);
    const [viewMode, setViewMode] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [attachmentsVariantsOpen, setAttachmentsVariantsOpen] = useState(false);
    const showModal = status !== "idle";

    const onAddVariant = async () => {
        //PlaybookStore.update(s => {
        //    s.selectedTemplate = row;
        //})

        setAddVariantDialogOpen(true);
    }

    const handleConfirmVariantDelete = async (row: CadenceTemplateVariant) => {
        try {
            const value = await execute();
            const response = await client.delete<ApiResponseType>('/api/playbooks/templates', { params: { templateLibraryId: row.id } });

            if (response.data.succeeded) {
                await loadPlaybooks('');

                ApplicationStore.update(s => {
                    s.snackbarMessage = "Template library variant successfully deleted";
                    s.snackbarOpen = true;
                });
            } else {
                alert('Error while deleting template library variant.');
            }
        } catch (err) {
            console.log(err);
        } finally {
            reset();
        }
    };

    const onSaveVariant = async (row: CadenceTemplateVariant) => {
        if (template === null || playbook === null) throw 'Playbook not selected';

        const templateWithContent = await client.get('/api/playbooks/templates', { params: { id: template.id } });

        const newRecord = { ...row, id: crypto.randomUUID(), deliverTo: 'lead', templateBody: templateWithContent.data.templateBody, templateTypeId: template.templateTypeId, subject: templateWithContent.data.subject };
        const response = await saveTemplateVariant(newRecord, playbook, template, '');


        if (response.succeeded) {
            setAddVariantDialogOpen(false);
            onEditTemplateVariant(newRecord);

        }

        return response;
    }

    const onEditTemplateVariant = async (row: CadenceTemplateVariant) => {
        const response = await client.get('/api/playbooks/templates', { params: { id: row.id } });

        PlaybookStore.update(s => {

            s.selectedTemplateVariant = response.data;
        })

        setEditTemplateVariantOpen(true);
    }

    const onCloseEditTemplateVariant = () => {
        PlaybookStore.update(s => {
            s.selectedTemplateVariant = null;
        })

        setEditTemplateVariantOpen(false);
        setViewMode(false);
    }

    const onOpenFilters = (event: MouseEvent, row: CadenceTemplateVariant) => {
        event.stopPropagation();

        PlaybookStore.update(s => {
            s.selectedTemplateVariant = row;
        })

        setFiltersOpen(true);
    }

    const onViewVariant = async (event: MouseEvent, row: CadenceTemplateVariant) => {
        event.stopPropagation();
        const response = await client.get('/api/playbooks/templates', { params: { id: row.id } });

        PlaybookStore.update(s => {
            s.selectedTemplateVariant = response.data;
        })


        setViewMode(true);
        setEditTemplateVariantOpen(true);
    }

    const onOpenVariantAttachments = (event: MouseEvent, row: CadenceTemplateVariant) => {
        event.stopPropagation();

        PlaybookStore.update(s => {
            s.selectedTemplateVariant = row;
        })

        setAttachmentsVariantsOpen(true);
    }

    const onCloseVariantAttachments = () => {
        PlaybookStore.update(s => {
            s.selectedTemplateVariant = null;
        })

        setAttachmentsVariantsOpen(false);
    }

    const onFiltersClose = () => {
        PlaybookStore.update(s => {
            s.selectedTemplateVariant = null;
        })

        setFiltersOpen(false);
    }

    if (!template) return null;

    return (
        <SimpleDialog open={open} onClose={onClose}>
            <Card sx={{ mb: 6 }}>
                <CardHeader title={`Template Variants for:  ${template.name}`} titleTypographyProps={{ variant: 'h6' }} action={<>
                    <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.AddTemplateVariants}>
                        <Button onClick={onAddVariant} variant='contained' sx={{ mr: 1 }} style={{ color: 'white' }} size="small" endIcon={<AddIcon />}>
                            Add Variant
                        </Button>
                    </RenderIf>
                    <Button onClick={onClose} variant='contained' sx={{ mr: 1 }} style={{ color: 'white' }} size="small" endIcon={<CloseIcon />}>
                        Close
                    </Button>
                </>
                } />

                <Divider sx={{ m: 0 }} />

                <TableContainer>
                    <Table size='small' sx={{ minWidth: 500 }}>
                        <TableHead
                            sx={{ backgroundColor: theme => (theme.palette.mode === 'light' ? 'grey.50' : 'background.default') }}
                        >
                            <TableRow>
                                <TableCell sx={{ height: '3.375rem' }}>Name</TableCell>
                                <TableCell sx={{ height: '3.375rem' }}>Template Type</TableCell>
                                <TableCell sx={{ height: '3.375rem', textAlign: 'center' }}></TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {template?.variants.map((item, index: number) => (
                                <TableRow hover key={index} sx={{ '&:last-of-type td': { border: 0 } }}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{item.name}</Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex' }}>
                                            <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{item.templateType}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ width: 100, textAlign: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.DeleteTemplateVariantFilters}>
                                                <IconButton onClick={() => handleConfirmVariantDelete(item)} title="Delete Template Library">
                                                    <DeleteIcon fontSize='small' />
                                                </IconButton>
                                            </RenderIf>
                                            <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.ViewTemplateVariants}>
                                                <Tooltip title='View'>
                                                    <Box>
                                                        <IconButton size='small' component='a' sx={{ textDecoration: 'none' }} onClick={(event: MouseEvent) => onViewVariant(event, item)}>
                                                            <EyeOutline fontSize='small' />
                                                        </IconButton>
                                                    </Box>
                                                </Tooltip>
                                            </RenderIf>
                                            <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.EditTemplateVariants}>
                                                <Tooltip title='Edit'>
                                                    <Box>
                                                        <IconButton size='small' component='a' sx={{ textDecoration: 'none' }} onClick={(event: MouseEvent) => onEditTemplateVariant(item)}>
                                                            <EditIcon fontSize='small' />
                                                        </IconButton>
                                                    </Box>
                                                </Tooltip>
                                            </RenderIf>
                                            <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.AddTemplateVariantAttachments}>
                                                <Tooltip title='Attachments'>
                                                    <Box>
                                                        <IconButton size='small' component='a' sx={{ textDecoration: 'none' }} onClick={(event: MouseEvent) => onOpenVariantAttachments(event, item)}>
                                                            <AttachFileIcon fontSize='small' />
                                                        </IconButton>
                                                    </Box>
                                                </Tooltip>
                                            </RenderIf>
                                            <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.ViewTemplateVariantFilters}>
                                                <Tooltip title='Filters'>
                                                    <Box>
                                                        <IconButton size='small' component='a' sx={{ textDecoration: 'none' }} onClick={(event: MouseEvent) => onOpenFilters(event, item)}>
                                                            <FilterAltIcon fontSize='small' />
                                                        </IconButton>
                                                    </Box>
                                                </Tooltip>
                                            </RenderIf>

                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            {addVariantDialogOpen && <NameDialog open={addVariantDialogOpen} onClose={() => setAddVariantDialogOpen(false)} onSubmit={onSaveVariant} title="Create Variant" subTitle="Please enter a name for the template variant" fieldLabel="Template Variant Name" confirmButtonLabel="Create" record={{ name: '' } as CadenceTemplateVariant} />}
            {editTemplateVariantOpen && <EditCadenceTemplateVariant open={editTemplateVariantOpen} onClose={onCloseEditTemplateVariant} viewMode={viewMode} />}
            {attachmentsVariantsOpen && <AttachmentVariantList open={attachmentsVariantsOpen} onClose={onCloseVariantAttachments} />}
            {filtersOpen && <VariantFilters open={filtersOpen} onClose={onFiltersClose} />}
            <ConfirmationDialog open={showModal} onConfirm={resolve} onCancel={reject} title="Delete?" message="Are you sure you want to delete the record?" confirmButtonText="Delete" cancelButtonText="Cancel" />
        </SimpleDialog>
    )
}

export default VariantListDialog